import { GlobalLayout, useIsLargerThanIpadResolution, InPageNavVisibilityProvider } from "@octopusdeploy/design-system-components";
import type { AreaNavListLinkItem, NavigationBarActionData, SideNavBarTabLink } from "@octopusdeploy/design-system-components";
import { AnalyticLinkLocationProvider } from "@octopusdeploy/portal-analytics";
import { nameForLink, useDispatchLinkClicked } from "@octopusdeploy/portal-routes";
import { exhaustiveCheck, isNotNull } from "@octopusdeploy/type-utils";
import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { WelcomeDialog } from "~/areas/dashboard/Onboarding/WelcomeDialog/WelcomeDialog";
import type { ProjectStatus } from "~/areas/projects/components/ProjectStatus/useProjectStatus";
import { session } from "~/clientInstance";
import DeprecationNotificationsPoller from "~/components/Navbar/NotificationPoller/DeprecationNotificationsPoller";
import LicenceNotificationPoller from "~/components/Navbar/NotificationPoller/LicenceNotificationPoller";
import PageLayoutUpliftNotifications from "~/components/Navbar/NotificationPoller/PageLayoutUpliftNotification";
import UpdateAvailableNotificationPoller from "~/components/Navbar/NotificationPoller/UpdateAvailableNotificationPoller";
import type { SpaceContext } from "~/components/SpaceLoader/SpaceLoader";
import { isSpaceNotFound, isSpecificSpaceContext } from "~/components/SpaceLoader/SpaceLoader";
import SystemMessagesBanner from "~/components/SystemMessagesBanner/SystemMessagesBanner";
import SearchAndOpenPopover from "~/globalSearch/SearchAndOpenPopover";
import { usePageArea } from "~/routing/PageAreaContext";
import { ContextualHelpPanel, useIsContextualHelpPanelExpanded } from "../ContextualHelpLayout/ContextualHelpLayout";
import { HelpNavigationActionButton } from "../Navbar/HelpNavigationActionButton";
import { NavigationBanner } from "../Navbar/NavigationBanner";
import LicenceUsageNotificationPoller from "../Navbar/NotificationPoller/LicenceUsageNotificationPoller";
import TargetTagsNotifications from "../Navbar/NotificationPoller/TargetTagsNotifications";
import NotificationsMenu from "../Navbar/NotificationsMenu";
import { SpaceSwitcherNavigationBarItem } from "../Navbar/SpaceSwitcherNavigationBarItem";
import type { AreaNavPanelConfig } from "./AreaNavPanelConfig";
import { UnhandledErrorPanel } from "./UnhandledErrorPanel";
import { useBottomSideNavBarItems } from "./useBottomSideNavBarItems";
import { useDeployAreaNavPanel } from "./useDeployAreaNavPanel";
import { usePlatformHubAreaNavPanel } from "./usePlatformHubAreaNavPanel";
import { useTopSideBarNavItems } from "./useTopSideNavBarItems";
interface PortalGlobalLayoutProps {
    spaceContext: SpaceContext;
    projectStatus: ProjectStatus;
    children: React.ReactNode;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
}
export function PortalGlobalLayout({ spaceContext, projectStatus, children, scrollAreaRef }: PortalGlobalLayoutProps) {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    const actions: NavigationBarActionData[] = [...getNavigationActions(isLargerThanIpad)];
    const [isInPageNavVisible, setIsInPageNavVisible] = React.useState(false);
    const areaNavPanelDisplayMode = isInPageNavVisible ? "overlay" : "inline";
    const areaNavPanelData = usePortalAreaNavPanelData(spaceContext);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const currentUser = session.currentUser!;
    const topSideBarNavItems = useTopSideBarNavItems(spaceContext);
    const bottomSideBarNavItems = useBottomSideNavBarItems(currentUser, spaceContext);
    const spaceId = isSpecificSpaceContext(spaceContext) ? spaceContext.Id : null;
    const dispatchLinkClicked = useDispatchLinkClicked();
    const handleSideNavBarLinkClicked = useCallback((item: SideNavBarTabLink) => {
        dispatchLinkClicked(nameForLink(), {
            linkLabel: "label" in item ? item.label : item.accessibleName,
            documentUrl: typeof item.href === "string" ? item.href : item.href.resolveWithSpaceId(spaceId),
            linkLocation: "Main Navigation",
        });
    }, [dispatchLinkClicked, spaceId]);
    const handleAreaNavPanelLinkClicked = useCallback((item: AreaNavListLinkItem) => {
        dispatchLinkClicked(nameForLink(), {
            linkLabel: item.label,
            documentUrl: typeof item.href === "string" ? item.href : item.href.resolveWithSpaceId(spaceId),
            linkLocation: "Area Nav Panel",
        });
    }, [dispatchLinkClicked, spaceId]);
    const location = useLocation();
    const prevPathnameRef = React.useRef(location.pathname);
    useEffect(() => {
        if (prevPathnameRef.current !== location.pathname && scrollAreaRef.current && typeof scrollAreaRef.current.scrollTo === "function") {
            scrollAreaRef.current.scrollTo({ top: 0 });
            prevPathnameRef.current = location.pathname;
        }
    }, [location, scrollAreaRef]);
    const isHelpExpanded = useIsContextualHelpPanelExpanded();
    return (<InPageNavVisibilityProvider isVisible={isInPageNavVisible} setIsVisible={setIsInPageNavVisible}>
            <GlobalLayout scrollAreaRef={scrollAreaRef} areaNavPanel={{
            items: areaNavPanelData.items,
            title: areaNavPanelData.title,
            badge: areaNavPanelData.badge,
            displayMode: areaNavPanelDisplayMode,
            onLinkClicked: handleAreaNavPanelLinkClicked,
        }} topNavBar={{
            logo: isSpaceNotFound(spaceContext) ? undefined : <SpaceSwitcherNavigationBarItem spaceContext={spaceContext}/>,
            searchBar: isSpaceNotFound(spaceContext) ? undefined : <SearchAndOpenPopover isFullWidth={!isLargerThanIpad} projectStatus={projectStatus}/>,
            banner: <NavigationBanner />,
            actions,
        }} sideNavBar={{
            topItems: topSideBarNavItems,
            bottomItems: bottomSideBarNavItems,
            onLinkClicked: handleSideNavBarLinkClicked,
        }} helpPanel={{
            isExpanded: isHelpExpanded,
            content: <ContextualHelpPanel />,
        }}>
                <UpdateAvailableNotificationPoller />
                <LicenceNotificationPoller />
                <LicenceUsageNotificationPoller />
                <PageLayoutUpliftNotifications />
                <TargetTagsNotifications />
                <DeprecationNotificationsPoller />
                <WelcomeDialog projectStatus={projectStatus}/>
                <AnalyticLinkLocationProvider location="Main Navigation Error">
                    <UnhandledErrorPanel />
                </AnalyticLinkLocationProvider>
                <SystemMessagesBanner />
                {children}
            </GlobalLayout>
        </InPageNavVisibilityProvider>);
}
export function usePortalAreaNavPanelData(spaceContext: SpaceContext): AreaNavPanelConfig {
    const pageArea = usePageArea();
    const deployAreaNavPanel = useDeployAreaNavPanel(spaceContext);
    const platformHubAreaNavPanel = usePlatformHubAreaNavPanel();
    if (!pageArea) {
        return { items: [] };
    }
    switch (pageArea.mainArea) {
        case "Deploy":
            return deployAreaNavPanel ?? { items: [] };
        case "Platform Hub":
            return platformHubAreaNavPanel ?? { items: [] };
        case "Tasks":
        case "Approvals":
        case "User Profile":
        case "Dev Tools":
        case "Configuration":
        case "Insights":
            return { items: [] };
        default:
            exhaustiveCheck(pageArea, "Not all page areas have been handled");
    }
}
function getNavigationActions(isLargerThanIpad: boolean): NavigationBarActionData[] {
    return [{ key: "notifications", content: <NotificationsMenu /> }, isLargerThanIpad ? { key: "help-menu", content: <HelpNavigationActionButton /> } : null].filter(isNotNull);
}
